import {useState, useRef, useEffect} from 'react';

type Mode = 'search' | 'commands';

export function useKeyboardNavigation(isOpen: boolean, mode: Mode, debouncedSearchValue: string) {
    const [activeIndex, setActiveIndex] = useState(-1);
    const resultRefs = useRef<(HTMLElement | null)[]>([]);

    useEffect(() => {
        setActiveIndex(-1);
    }, [debouncedSearchValue]);

    useEffect(() => {
        if (!isOpen || mode !== 'commands') return;

        const handleKeyDown = (e: KeyboardEvent) => {
            const refs = resultRefs.current.filter(
                (element): element is HTMLElement =>
                    element instanceof HTMLElement && element.isConnected
            );
            const maxIndex = refs.length - 1;

            if (maxIndex < 0) return;

            switch (e.key) {
                case 'ArrowDown':
                case 'Down': {
                    e.preventDefault();
                    setActiveIndex((prev) => {
                        const next = prev < maxIndex ? prev + 1 : 0;
                        refs[next]?.scrollIntoView({block: 'nearest'});

                        return next;
                    });
                    break;
                }
                case 'ArrowUp':
                case 'Up': {
                    e.preventDefault();
                    setActiveIndex((prev) => {
                        const next = prev > 0 ? prev - 1 : maxIndex;
                        refs[next]?.scrollIntoView({block: 'nearest'});

                        return next;
                    });
                    break;
                }
                case 'Enter': {
                    e.preventDefault();
                    // If no item is actively selected, trigger the first result
                    const targetIndex = activeIndex >= 0 ? activeIndex : 0;
                    const el = refs[targetIndex];
                    if (el) {
                        const link =
                            el instanceof HTMLAnchorElement ? el : el.querySelector('a[href]');
                        if (link && link instanceof HTMLAnchorElement) {
                            link.click();
                        } else {
                            el.click();
                        }
                    }
                    break;
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, mode, activeIndex]);

    return {activeIndex, resultRefs};
}
